import { useEffect, useMemo } from 'react'

import { TeamMemberDto, TeamMemberRole } from '@api'
import { type ColumnDef } from '@tanstack/react-table'
import { useUnit } from 'effector-react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import {
  $numberAssignments,
  cleanupNumberAssignments,
} from '@/modules/automations/models/number-assignments'

import { MemberSelectValue } from './member-select-value'

interface NumberAssignmentsTableProps {
  maxNumbers: number
  teamMembers: TeamMemberDto[]
  disabled?: boolean
  ableToEdit?: boolean
}

interface NumberAssignment {
  number: number
  assignedMembers: string[]
}

export const NumberAssignmentsTable = ({
  maxNumbers,
  teamMembers,
  disabled,
  ableToEdit,
}: NumberAssignmentsTableProps) => {
  const numberAssignments = useUnit($numberAssignments)

  const numbers = useMemo(
    () =>
      Array.from({ length: maxNumbers }, (_, i) => {
        const numberIndex = i + 1
        const assignedTeamMembers = numberAssignments[numberIndex] || []

        return {
          number: numberIndex,
          assignedMembers: assignedTeamMembers.map((member) => String(member)),
        }
      }),
    [maxNumbers, numberAssignments],
  )

  useEffect(() => {
    cleanupNumberAssignments(maxNumbers)
  }, [maxNumbers])

  const teamMemberOptions = useMemo(
    () =>
      teamMembers
        .filter((member) => member.role === TeamMemberRole.MEMBER)
        .map((member) => ({
          label: member.name,
          value: String(member.userId),
          disabled: !ableToEdit,
        })),
    [teamMembers, ableToEdit],
  )

  const columns = useMemo<ColumnDef<NumberAssignment>[]>(
    () => [
      {
        id: 'number',
        header: 'Number',
        size: 100,
        cell: ({ row }) => (
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary-400 text-white">
            {row.original.number}
          </div>
        ),
      },
      {
        id: 'assignedMembers',
        header: () => (
          <div className="w-full flex items-center gap-2">
            Assigned Members
            <Tooltip
              sideOffset={20}
              content="Select chatters who can interact with fans assigned to this number"
            >
              <InfoIcon className="text-secondary-300 h-4 w-4" />
            </Tooltip>
          </div>
        ),
        cell: ({ row }) => (
          <MemberSelectValue
            disabled={!!disabled}
            teamMemberOptions={teamMemberOptions}
            number={row.original.number}
          />
        ),
      },
    ],
    [disabled, teamMemberOptions],
  )

  return (
    <div className="w-full">
      <h2 className="text-xl font-semibold mb-4">Number Assignments</h2>
      <DataTable
        data={numbers}
        columns={columns}
      />
    </div>
  )
}
